import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCatalog } from '../../../context/CatalogContext';
import StatGroupCard from './StatGroupCard';

/* The marks a coach reads, grouped the way they read them.

   Driven entirely by the sport catalog — a swimmer's groups are not a
   quarterback's, and nothing here names a sport. Ported from `statGroups` /
   `measurableGroups` in RecruitMeView.swift. */

/* The order a coach reads a body in: how big, how fast, how explosive, how well
   they change direction, how strong. Agility precedes Strength deliberately —
   weight-room numbers are the last thing read, not the fourth.

   This is a CATEGORY ranking, not a measurable list, so it stays sport-neutral:
   categories the catalog defines that this list doesn't name (Sport-Specific,
   and whatever a future sport adds) follow in catalog order rather than being
   dropped. */
const GROUP_ORDER = ['Size', 'Speed', 'Power', 'Agility', 'Strength'];

const rank = (category) => {
  const i = GROUP_ORDER.indexOf(category);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

const isFilled = (m) => m.value != null && m.value !== '';

/** Filled marks sort ahead of empty ones so a scroller opens on real data. */
const filledFirst = (marks) => [
  ...marks.filter(isFilled),
  ...marks.filter((m) => !isFilled(m)),
];

function academicMarks(profile) {
  if (!profile) return [];
  const num = (v, digits) => (v == null || v === '' ? null : Number(v).toFixed(digits));
  return [
    { key: 'gpa_weighted', label: 'GPA WTD', value: num(profile.gpa_weighted, 2), unit: null, verified: !!profile.gpa_weighted_verified },
    { key: 'gpa_unweighted', label: 'GPA UNWTD', value: num(profile.gpa_unweighted, 2), unit: null, verified: !!profile.gpa_unweighted_verified },
    { key: 'sat', label: 'SAT', value: profile.sat_score == null || profile.sat_score === '' ? null : String(profile.sat_score), unit: null, verified: !!profile.sat_score_verified },
    { key: 'act', label: 'ACT', value: profile.act_score == null || profile.act_score === '' ? null : String(profile.act_score), unit: null, verified: !!profile.act_score_verified },
  ];
}

export default function StatGroups({ profile }) {
  const navigate = useNavigate();
  const catalog = useCatalog();

  const sportSlug = catalog.sportSlugFor(profile?.sport);
  const catalogGroups = profile?.sport ? catalog.groupedMeasurablesFor(sportSlug) : [];

  // Saved marks, keyed. First win on duplicates, matching the apps.
  const saved = {};
  (profile?.measurables || []).forEach((m) => {
    if (!(m.key in saved)) saved[m.key] = m;
  });

  const groups = [...catalogGroups]
    .sort((a, b) => rank(a.category) - rank(b.category))
    .map((g) => ({
      name: g.category || 'Other',
      marks: filledFirst(g.measurables.map((def) => {
        const hit = saved[def.key];
        return {
          key: def.key,
          label: def.label,
          value: hit ? hit.value : null,
          unit: def.unit,
          verified: hit ? !!hit.verified : false,
        };
      })),
      section: 'measurables',
    }));

  const academics = academicMarks(profile);
  if (academics.length) {
    groups.push({ name: 'Academics', marks: filledFirst(academics), section: 'academics' });
  }

  if (groups.length === 0) {
    return <p className="me-empty-note">Set your sport to see the marks coaches look for.</p>;
  }

  return (
    <>
      {groups.map((g) => {
        const filled = g.marks.filter(isFilled).length;
        const count = filled === 0 ? 'nothing logged' : `${filled} of ${g.marks.length}`;
        return (
          <div className="me-group" key={g.name}>
            <div className="me-group__head">
              <span className="me-group__name">{g.name}</span>
              <span className="me-group__count">{count}</span>
            </div>
            <StatGroupCard
              marks={g.marks}
              onOpen={() => navigate(g.section === 'academics' ? '/academics' : '/measurables')}
            />
          </div>
        );
      })}
    </>
  );
}
